import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/ui/AppIcon';

/* =========================================================================
   Modal para crear bloque (Componente Aislado)
   ========================================================================= */
export default function CreateBlockModal({ open, onClose, onCreate }) {
  const [category, setCategory] = useState('electrical');
  const [blockType, setBlockType] = useState('standard_comparison');
  const [mode, setMode] = useState('VDC');
  const [unit, setUnit] = useState('V');
  const [rangeText, setRangeText] = useState('');
  const [displayName, setDisplayName] = useState('');

  const BLOCK_TYPE_CATALOG = useMemo(
    () => ({
      electrical: [{ value: 'standard_comparison', label: 'Medición Estándar (V/A/Ω)' }],
      pressure: [{ value: 'pressure_hysteresis', label: 'Prueba de Presión con Histéresis' }],
      force: [{ value: 'force_comparison', label: 'Prueba de Fuerza / Torque' }],
      temperature: [
        { value: 'standard_comparison', label: 'Medición Estándar (Termómetro)' },
        { value: 'chamber_mapping', label: 'Mapeo de Cámara (Estufa/Freezer)' },
      ],
      mass: [
        { value: 'standard_comparison', label: 'Prueba de Linealidad' },
        { value: 'repeatability', label: 'Prueba de Repetibilidad' },
        { value: 'eccentric_load', label: 'Prueba de Carga Excéntrica' },
      ],
      dimensional: [
        { value: 'dimensional_comparison', label: 'Medición Dimensional (Calibre)' },
        { value: 'parallelism_flatness', label: 'Prueba de Paralelismo y Planeidad' },
      ],
    }),
    []
  );

  const UNIT_CATALOG = useMemo(
    () => ({
      electrical: ['mV', 'V', 'mA', 'A', 'Ω', 'kΩ', 'MΩ', 'Hz', 'kHz'],
      pressure: ['mbar', 'bar', 'psi', 'Pa', 'kPa', 'MPa'],
      force: ['N', 'kN', 'kgf', 'lbf', 'Nm'],
      temperature: ['°C', '°F', 'K'],
      mass: ['mg', 'g', 'kg'],
      dimensional: ['µm', 'mm', 'cm', 'm'],
    }),
    []
  );

  const blockTypeOptions = useMemo(() => BLOCK_TYPE_CATALOG[category] || [], [category, BLOCK_TYPE_CATALOG]);
  const unitOptions = useMemo(() => UNIT_CATALOG[category] || [], [category, UNIT_CATALOG]);

  useEffect(() => {
    if (!open) {
      setCategory('electrical');
      setBlockType('standard_comparison');
      setMode('VDC');
      setUnit('V');
      setRangeText('');
      setDisplayName('');
    }
  }, [open]);

  useEffect(() => {
    setBlockType(blockTypeOptions[0]?.value || 'standard_comparison');
    setUnit(unitOptions[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  if (!open) return null;

  const handleCreate = () => {
    const payload = {
      block_type: blockType,
      mode: category === 'electrical' ? mode : 'CUSTOM',
      unit,
      range_text: rangeText || null,
      display_name: displayName || null,
    };
    onCreate(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Agregar Bloque de Prueba</h3>
          <button className="text-gray-500 hover:text-gray-900" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <label className="text-sm font-medium">Categoría del Instrumento</label>
            <select
              className="mt-1 w-full rounded-md border px-3 py-2"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="electrical">Eléctrica</option>
              <option value="pressure">Presión</option>
              <option value="temperature">Temperatura</option>
              <option value="mass">Masa (Balanzas)</option>
              <option value="dimensional">Dimensional</option>
            </select>
          </div>

          <div className="col-span-1">
            <label className="text-sm font-medium">Tipo de Prueba</label>
            <select
              className="mt-1 w-full rounded-md border px-3 py-2"
              value={blockType}
              onChange={(e) => setBlockType(e.target.value)}
            >
              {blockTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {category === 'electrical' && (
            <div className="col-span-1">
              <label className="text-sm font-medium">Variable / Modo</label>
              <select
                className="mt-1 w-full rounded-md border px-3 py-2"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                <option value="VDC">Voltaje DC</option>
                <option value="VAC">Voltaje AC</option>
                <option value="ADC">Corriente DC</option>
                <option value="AAC">Corriente AC</option>
                <option value="OHM">Resistencia</option>
                <option value="HZ">Frecuencia</option>
              </select>
            </div>
          )}

          <div className={category === 'electrical' ? 'col-span-1' : 'col-span-2'}>
            <label className="text-sm font-medium">Unidad Principal</label>
            <select
              className="mt-1 w-full rounded-md border px-3 py-2"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            >
              {unitOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium">Nombre de la Prueba (ej. Tensión Continua)</label>
            <input
              className="mt-1 w-full rounded-md border px-3 py-2"
              placeholder="Voltaje DC, Prueba de Linealidad, Mapeo a 50°C"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium">Rango (texto opcional)</label>
            <input
              className="mt-1 w-full rounded-md border px-3 py-2"
              placeholder="Ej.: 0–20V, 0–10 bar, 50mm - 150mm"
              value={rangeText}
              onChange={(e) => setRangeText(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button className="border rounded px-3 py-2" onClick={onClose}>
            Cancelar
          </button>
          <button className="bg-blue-600 text-white rounded px-3 py-2" onClick={handleCreate}>
            Crear Bloque
          </button>
        </div>
      </div>
    </div>
  );
}
